import { Injectable, OnDestroy } from '@angular/core';
import { IPurchasesInvoices } from '../../../../../@interfaces/purchaseInvoice.interface';
import {
  UntypedFormGroup,
  UntypedFormArray,
  UntypedFormControl,
} from '@angular/forms';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { PurchasesService } from '../../../../../@pages/purchases/purchases.service';
import { ITributaryValues } from '../../../../../@interfaces/tributary.interface';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ConverseFilesService } from '../../../services/files/converse-files.service';
import { ICities } from '../../../../../@interfaces/cities.interface';
import { NgxSpinnerService } from 'ngx-spinner';
import { IProvider } from 'src/app/@interfaces/provider.interface';

@Injectable({
  providedIn: 'root',
})
export class RegisterPurchaseService implements OnDestroy {
  unsubscribe$ = new Subject<void>();
  citiesList: ICities[] = [];
  constructor(
    private translate: TranslateService,
    private purchasesService: PurchasesService,
    private converseFilesService: ConverseFilesService,
    private spinnerService: NgxSpinnerService,
  ) {}

  setQuotationData(
    purchaseForm: UntypedFormGroup,
    rowsArr: UntypedFormArray,
    newProvider: UntypedFormControl,
  ) {
    const isDraft = purchaseForm.value.is_draft;
    let setPurchaseInvoice = {} as IPurchasesInvoices;
    let total = 0;

    setPurchaseInvoice.tax = purchaseForm.value.tax;
    setPurchaseInvoice.provider = {} as IProvider;
    setPurchaseInvoice.provider.id_provider = parseInt(
      newProvider.value.id_provider,
    );
    setPurchaseInvoice.createdAt = purchaseForm.value.date;
    setPurchaseInvoice.dueDate = purchaseForm.value.dueDate;
    setPurchaseInvoice.tax_incl = new Boolean(
      purchaseForm.value.tax_include,
    ).toString();
    setPurchaseInvoice.clause = new Boolean(
      purchaseForm.value.clause,
    ).toString();
    setPurchaseInvoice.is_electronic_document =
      purchaseForm.value.is_electronic_document;
    setPurchaseInvoice.is_draft = purchaseForm.value.is_draft;
    setPurchaseInvoice.contpurchase = { written_invoice: '' };
    setPurchaseInvoice.contpurchase!.written_invoice =
      purchaseForm.value.written_invoice;

    for (let x = 0; x < rowsArr.value.length; x++) {
      total = total + parseFloat(rowsArr.value[x].subtotal);
    }

    setPurchaseInvoice.purchases = [
      {
        id_purchases: [],
        id_warehouse: [],
        codigo: [],
        quantity: [],
        price: [],
        discount: [],
        subtotal: [],
        total: [],
        id_article: [],
        id_purchase_unit: [],
        quantity_in_base_unit: [],
      },
    ];

    for (let index = 0; index < rowsArr.value.length; index++) {
      const row = rowsArr.value[index];

      if (!isDraft) this.validItems(row.article?.codigo);

      setPurchaseInvoice.purchases[0].id_warehouse.push(
        parseInt(row.warehouse),
      );
      setPurchaseInvoice.purchases[0].codigo.push(row.article?.codigo || null);
      setPurchaseInvoice.purchases[0].quantity.push(
        parseFloat(row.quantity) || 0,
      );
      setPurchaseInvoice.purchases[0].price.push(parseFloat(row.price) || 0);
      setPurchaseInvoice.purchases[0].discount.push(
        parseFloat(row.discount) || 0,
      );
      setPurchaseInvoice.purchases[0].subtotal.push(
        parseFloat(row.subtotal) || 0,
      );
      setPurchaseInvoice.purchases[0].total.push(total);
      setPurchaseInvoice.purchases[0].id_article.push(
        row.article?.id_articulo || null,
      );
      setPurchaseInvoice.purchases[0].id_purchase_unit!.push(
        parseInt(row.unit) || null,
      );
      setPurchaseInvoice.purchases[0].quantity_in_base_unit!.push(
        parseFloat(row.quantityInBaseUnit || row.quantity) || 0,
      );
    }

    return setPurchaseInvoice;
  }

  validItems(item: any) {
    if (!item) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: this.translate.instant('Some of the items do not exist'),
      });
      this.spinnerService.hide();
      return;
    }
  }

  async converseCities(customerCity: string, providerCity: string) {
    const validCustomerCity = this.converseFilesService.validText(customerCity);
    const validProviderCity = this.converseFilesService.validText(providerCity);
    const parsedCustomerCity = validCustomerCity
      ? await this.converseFilesService.converseUTF8(
          this.converseFilesService.removeAccents(customerCity),
        )
      : await this.converseFilesService.converseUTF8(customerCity);
    const parsedProviderCity = validProviderCity
      ? await this.converseFilesService.converseUTF8(
          this.converseFilesService.removeAccents(providerCity),
        )
      : await this.converseFilesService.converseUTF8(providerCity);
    return {
      parsedCustomerCity: parsedCustomerCity.trim(),
      parsedProviderCity: parsedProviderCity.trim(),
    };
  }

  getInvoiceCities(invoice: any) {
    const customerCity =
      invoice.Invoice['cac:AccountingCustomerParty']['cac:Party'][
        'cac:PhysicalLocation'
      ]['cac:Address']['cbc:CityName']['cbc:CityName'];
    const providerCity =
      invoice.Invoice['cac:AccountingSupplierParty']['cac:Party'][
        'cac:PhysicalLocation'
      ]['cac:Address']['cbc:CityName']['cbc:CityName'];
    return { customerCity: customerCity, providerCity: providerCity };
  }

  getTaxBase() {
    try {
      return new Promise<ITributaryValues[]>(async (resolve, reject) => {
        resolve(await this.getTributaryValues(1));
      });
    } catch (error) {
      throw error;
    }
  }

  getTributaryValues(id_value: number) {
    return new Promise<ITributaryValues[]>((resolve, reject) => {
      this.purchasesService
        .getSingleTributaryValues(id_value)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (result: ITributaryValues[]) => {
            resolve(result);
          },
          (error) => {
            reject(error);
          },
        );
    });
  }

  calculateTaxesFromBackend(
    rowsArr: UntypedFormArray,
    newProvider: UntypedFormControl,
    customerCity: string,
    providerCity: string,
    clause: boolean,
  ): Promise<{ withholdingTax: number; icaTax: number }> {
    const lines = (rowsArr.value as any[]).filter((e) => e.article);
    const articleCode = lines.map((e) => e.article.codigo as string);
    const quantity = lines.map((e) => parseFloat(e.quantity) || 0);
    const price = lines.map((e) => parseFloat(e.price) || 0);
    const discount = lines.map((e) => parseFloat(e.discount) || 0);
    const providerID = parseInt(newProvider.value?.id_provider, 10);
    return new Promise((resolve, reject) => {
      this.purchasesService
        .previewWithholdings(
          providerID,
          articleCode,
          quantity,
          price,
          discount,
          customerCity,
          providerCity,
          clause,
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (result) => resolve(result ?? { withholdingTax: 0, icaTax: 0 }),
          error: (err) => reject(err),
        });
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
